import styled, { css } from 'styled-components';
import { Handle } from 'react-flow-renderer';
import type { ContainerRelationship, NodeState } from 'flow/service/graph';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  CHILD_NODE_WIDTH,
  LEFT_CHILD_PADDING,
} from 'flow/service/graph';
import { StyledFolderHat } from '../FolderNode/styles';
import { containerRelatedStyle, containerFocusedStyle, containerFocusedAddedOrModifiedStyle } from '../SnippetNode/styles';
import type { SnippetOperation } from 'flow/store/redux/types';
import type { Operation as MergeDiffOperation } from 'flow/service/changelog/types';
import { isAddedOrModifiedBySnippetUpdate } from './helpers';


const hoveredStyles = css`
  cursor: pointer;
`;

const focusedStyles = css`
  cursor: default;
  border: 1px solid var(--cl-juicyBlue-500);
  box-shadow:0px 0px 0px 4px color-mix(in srgb, var(--cl-juicyBlue-100) 80%, transparent);
`;

const focusedHoveredStyles = css`
  cursor: pointer;
  border: 1px solid var(--cl-juicyBlue-500);
  box-shadow:0px 0px 0px 4px color-mix(in srgb, var(--cl-juicyBlue-100) 80%, transparent);
`;

const fadedStyles = css`
  cursor: default;
  opacity: 0.35;
`;

const deemphasizedStyles = css`
  opacity: 0.65;
`;

const addedOrModifiedStyles = css`
  border: 1px solid var(--cl-orange-400);
`;

type StyledBasedNodeProps = {
  nodeState: NodeState;
  baseBackgroundColor: string;
  isOutsideCanvas?: boolean;
  isChildNode?: boolean;
  isFolderNode?: boolean;
  containerRelationship?: ContainerRelationship;
  mergeDiffOperation?: MergeDiffOperation;
  snippetOperation?: SnippetOperation;
};

export const StyledBaseNode = styled.div<StyledBasedNodeProps>`
&&& {
  ${({ isOutsideCanvas, isChildNode }) => (isOutsideCanvas
    ? css`width: 100%;`
    : `width: ${isChildNode ? CHILD_NODE_WIDTH : NODE_WIDTH}px;`)}
  height: ${NODE_HEIGHT}px;
  object-fit: contain;
  overflow: hidden;
  color: var(--cl-gray-1100, #191919);
  font-feature-settings: 'clig' off, 'liga' off;
  text-overflow: ellipsis;
  padding: var(--cl-space-1-5);

  background-color: ${({ baseBackgroundColor }) => baseBackgroundColor};

  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; // todo - add line height variable
  display: flex;
  border-radius: var(--cl-radius-1);
  align-items: center;
  justify-content: center;
  transition: height 0.2s, width 0.2s ease-in-out;

  /* General base styles based on Node state */
  ${({ nodeState, containerRelationship }: StyledBasedNodeProps) => {
    if (nodeState === 'hovered') return hoveredStyles;
    if (nodeState === 'focused') return focusedStyles;
    if (nodeState === 'focused-hovered') return focusedHoveredStyles;
    if (nodeState === 'faded' && !containerRelationship) return fadedStyles;
    if (nodeState === 'deemphasized' && !containerRelationship) return deemphasizedStyles;
    return null;
  }}

  ${({ isFolderNode }: StyledBasedNodeProps) => isFolderNode && StyledFolderHat}

  ${({ containerRelationship, snippetOperation, mergeDiffOperation }: StyledBasedNodeProps) => {
    if (containerRelationship === 'related') {
      return css`${containerRelatedStyle}`;
    }
    if (containerRelationship === 'source') {
      const isAddedOrModified = isAddedOrModifiedBySnippetUpdate({
        snippetOperation,
        mergeDiffOperation,
      });
      return css`${isAddedOrModified ? containerFocusedAddedOrModifiedStyle : containerFocusedStyle}`;
    }
    return null;
  }}

  ${({ snippetOperation, mergeDiffOperation }: StyledBasedNodeProps) => {
    if (isAddedOrModifiedBySnippetUpdate({ snippetOperation, mergeDiffOperation })) {
      return addedOrModifiedStyles;
    }
    return null;
  }
}
`;

export const searchFocusedTextStyles = css`
  background-color: var(--cl-orange-400);
`;

export const searchUnfocusedTextStyles = css`
  background-color: var(--cl-yellow-300);
`;

type StyledNodeTextProps = {
  isChildNode?: boolean;
  isOutsideCanvas?: boolean;
  nodeState: NodeState;
};

export const NODE_TEXT_MAX_WIDTH = 140;

export const StyledNodeText = styled.span<StyledNodeTextProps>`
  ${({ isOutsideCanvas, isChildNode }) => {
    const maxWidth = isChildNode
      ? (NODE_TEXT_MAX_WIDTH - LEFT_CHILD_PADDING)
      : NODE_TEXT_MAX_WIDTH;
    return !isOutsideCanvas && `max-width: ${maxWidth}px;`;
  }}
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  /* General base styles based on Node state */
  ${({ nodeState }) => {
    if (nodeState === 'search-focused') return searchFocusedTextStyles;
    if (nodeState === 'search-unfocused') return searchUnfocusedTextStyles;
    return null;
  }}
`;

export const StyledLeftHandle = styled(Handle)`
&&& {
  visibility: hidden;
  left: 1px;
  top: ${NODE_HEIGHT / 2}px;
}
`;

export const StyledRightHandle = styled(Handle)`
&&& {
  visibility: hidden;
  right: 1px;
  top: ${NODE_HEIGHT / 2}px;
}
`;

export const StyledEvaluationSetTagNodeIndicator = styled.div`
  width: var(--cl-space-2);
  height: var(--cl-space-2);
  background-color: var(--cl-green-300);
  border-radius: 100%;
  flex: 0 0 var(--cl-space-2);
  z-index: 1;
`;

export const StyledAddedOrModifiedIndicator = styled.div`
  background-color: var(--cl-orange-400);
  border-radius: var(--cl-radius-circle);
  height: 8px;
  width: 8px;
`;
